import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Divider, LinearProgress, Grid } from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { t } from 'i18next';
import { markShipmentDepartedAction } from '../../../../actions/mangeActionV2';
import { showSuccessToast, showErrorToast } from '../../../../slices/toastSlice';

export default function ConfirmDepartureDialog({ open, onClose, row, onSave }) {
  const dispatch = useDispatch();
  const departState = useSelector((s) => s.manageV2?.markShipmentDeparted) || {};


  const summary = useMemo(() => {
    if (!row) return null;
    const totalContainers = row.containers?.length || 0;
    const totalVehicles = row.containers?.reduce((s, c) => s + (c.vehicles?.length || 0), 0) || 0;
    return {
      bookingNumber: row?.booking?.number,
      vesselName: row?.booking?.vesselName,
      voyageNumber: row?.booking?.voyageNumber,
      destination: row?.booking?.destination?.name,
      shippingLine: row?.booking?.shippingLine?.name,
      totalContainers,
      totalVehicles,
    };
  }, [row]);

  const handleConfirm = () => {
    if (!row) return;
    dispatch(markShipmentDepartedAction({ id: row.id }));
  };

  useEffect(() => {
    if (!open) return;
    if (departState.loading === 'succeeded') {
      dispatch(showSuccessToast(t('Shipment marked as departed')));
      onSave && onSave();
      onClose && onClose();
    } else if (departState.loading === 'failed') {
      dispatch(showErrorToast(departState.errorMessage || t('Failed to mark as departed')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departState.loading]);

  if (!row) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FlightTakeoffIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>{t('Confirm Shipment Departure')}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {summary && (
          <Box>
            <Grid container spacing={1.2} sx={{ p: .5 }}>
              <Grid item xs={12} md={4}><InfoField label={t('Booking')} value={summary.bookingNumber} /></Grid>
              <Grid item xs={12} md={4}><InfoField label={t('Vessel')} value={summary.vesselName} /></Grid>
              <Grid item xs={12} md={4}><InfoField label={t('Voyage')} value={summary.voyageNumber} /></Grid>
              <Grid item xs={12} md={4}><InfoField label={t('Destination')} value={summary.destination} /></Grid>
              <Grid item xs={12} md={4}><InfoField label={t('Shipping Line')} value={summary.shippingLine} /></Grid>
              <Grid item xs={12} md={4}><InfoField label={t('Containers')} value={summary.totalContainers} /></Grid>
              <Grid item xs={12} md={4}><InfoField label={t('Vehicles')} value={summary.totalVehicles} /></Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('You are about to mark this shipment as departed from the warehouse. This action will update its status accordingly.')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }}>{departState.loading === 'pending' && <LinearProgress sx={{ height: 4, borderRadius: 1 }} />}</Box>
        <Button onClick={onClose} disabled={departState.loading === 'pending'}>{t('cancel')}</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={departState.loading === 'pending'}>
          {t('Confirm Departed')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function InfoField({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: .25 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#546E7A' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value ?? '-'}</Typography>
    </Box>
  );
}
